import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Modal } from '@swyftx/aviary/atoms/Modal';
import { Separator } from '@swyftx/aviary/atoms/Seperator/Seperator';
import { Body, Numeric, Utility } from '@swyftx/aviary/atoms/Typography';
import { ReviewItem } from '@swyftx/aviary/molecules/ReviewItem/ReviewItem';
import { MarginHistoricOrder } from '@swyftx/margin/build/types';

import { Big } from '@shared/safe-big';
import { formatCurrency } from '@shared/utils';

import { capitalize } from '@utils/formatting';

import { DateTime } from 'luxon';
import { useMarginAccount } from 'src/lib/margin/hooks/account';
import { useMarginInstrumentDetails, useMarginInstruments } from 'src/lib/margin/hooks/instruments';

import { MarginInstrument } from '../../General';
import { MarginHistoryTableProfitLoss } from '../MarginPositionsCard/Tables/MarginHistoryTable/components';

type Props = {
  order: MarginHistoricOrder;
  open?: boolean;
  onClose?: () => void;
};

const MarginHistoryDetailsModal: React.FC<Props> = ({ order, open = false, onClose }) => {
  const [modalOpen, setModalOpen] = useState<boolean>(open);
  const { getMarketById } = useMarginInstruments();
  const { currency } = useMarginAccount();
  const market = useMemo(() => getMarketById(order.marketUuid), [getMarketById, order.marketUuid]);
  const { priceScale } = useMarginInstrumentDetails({ instrument: market });
  const { t } = useTranslation('margin');

  const subTitle = useMemo(() => {
    if (!order) return undefined;

    return (
      <FlexLayout alignItems='center' spacing={4}>
        <Utility variant='caption'>{t('position.title.id', { id: order.orderId })}</Utility>
        {order.dateTime && (
          <Utility variant='caption'>
            •{' '}
            {t('position.title.date', {
              date: DateTime.fromMillis(order.dateTime).toFormat('dd MMM yyyy hh:mm:ssa'),
            })}
          </Utility>
        )}
      </FlexLayout>
    );
  }, [order, t]);

  const title = useMemo(() => {
    if (!order) return undefined;

    return `${t(`tradeStatus.${order.status}` as any)}`;
  }, [order, t]);

  const volume = useMemo(() => order.filledVolume || order.volume, [order.filledVolume, order.volume]);

  return (
    <Modal
      position='dynamic'
      className='lg:min-w-[400px]'
      title={title}
      subTitle={subTitle}
      open={modalOpen}
      onOpenChange={(o) => {
        if (!o && onClose) {
          onClose();
          return;
        }
        setModalOpen(!!o);
      }}
      onClose={onClose}
    >
      <FlexLayout direction='column' className='px-24 pb-24' spacing={12}>
        {order?.orderType && (
          <ReviewItem title={t('position.orderType.title')}>
            <Body weight='bold'>{capitalize(order.orderType)}</Body>
          </ReviewItem>
        )}
        {market && (
          <ReviewItem title={t('position.instrument.title')}>
            <MarginInstrument market={market} size='medium' weight='bold' />
          </ReviewItem>
        )}
        {order?.side && (
          <ReviewItem title={t('position.side.title')}>
            <Body weight='bold'>{t(`tradeSide.${order.side}` as any)}</Body>
          </ReviewItem>
        )}
        {(order?.filledVolume || order?.volume) && (
          <ReviewItem title={t('position.sizeLots.title')}>
            <Numeric weight='bold'>
              {formatCurrency(!volume || Big(volume).eq(0) ? '-' : volume, undefined, { priceScale })}
            </Numeric>
          </ReviewItem>
        )}
        {(order?.fillPrice || order?.price || order?.stopLoss || order?.takeProfit) && (
          <ReviewItem title={t('position.price.title')}>
            <Numeric weight='bold'>
              {formatCurrency(order.fillPrice || order.price || order?.stopLoss || order?.takeProfit || '0')}
            </Numeric>
          </ReviewItem>
        )}
        {order.closePnl && !Big(order?.closePnl).eq(0) && (
          <ReviewItem
            underline='dashed'
            title={t('position.profitLoss.title')}
            tooltip={t('position.profitLoss.tooltip')}
          >
            <MarginHistoryTableProfitLoss order={order} />
          </ReviewItem>
        )}

        {order.commission && (
          <FlexLayout direction='column' spacing={12}>
            <Separator />
            <Utility variant='caption' weight='bold' color='primary'>
              {t('position.fees.title')}
            </Utility>
          </FlexLayout>
        )}
        {order.commission && !Big(order?.commission).eq(0) && (
          <ReviewItem
            underline='dashed'
            title={t('position.commission.title')}
            tooltip={t('position.commission.tooltip')}
          >
            <Numeric weight='bold' color='primary'>
              {formatCurrency(order.commission, currency, { appendCode: true, hideCode: false })}
            </Numeric>
          </ReviewItem>
        )}
      </FlexLayout>
    </Modal>
  );
};

export { MarginHistoryDetailsModal };
